// ═══════════════════════════════════════════════════════════
// CSV Redaction Editor
//
// Opened from DocumentsTab for CSV files via:
//   /staff/redact-csv/:docId
//
// Flow:
//   1. Fetch document record + download raw CSV text
//   2. Parse CSV into header + data rows
//   3. Staff selects target columns, match mode, scope, criteria
//   4. AI (batched) identifies values to redact
//   5. Redactions applied to working copy shown in table
//   6. Staff saves redacted copy back to GAB
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  IconArrowLeft, IconLoader2, IconCheck, IconAlertCircle,
  IconDeviceFloppy, IconX, IconPlayerPlay,
  IconTable,
} from '@tabler/icons-react';
import {
  getDocument, getStaffDownloadUrl, uploadRedactedVersion,
  type FoiaDocument,
} from '../../../api/foiaDocuments';
import { useAuthStore } from '../../../store/authStore';

// ── Types ─────────────────────────────────────────────────

type MatchMode  = 'exact' | 'ai';
type RedactScope = 'word' | 'cell';

interface RedactResult {
  index: number;   // row index within the batch
  redact: string[];
}

interface LogEntry {
  row: number;    // 1-based display row
  col: number;    // 1-based display col
  colName: string;
  original: string;
  result: string;
}

// ── CSV Parser ────────────────────────────────────────────

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let current = '';
  let inQuotes = false;
  let row: string[] = [];

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') { current += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { current += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { row.push(current); current = ''; }
      else if (ch === '\n' || (ch === '\r' && text[i + 1] === '\n')) {
        row.push(current); current = '';
        if (row.length > 1 || row[0] !== '') rows.push(row);
        row = [];
        if (ch === '\r') i++;
      } else { current += ch; }
    }
  }
  if (current || row.length > 0) {
    row.push(current);
    if (row.length > 1 || row[0] !== '') rows.push(row);
  }
  return rows;
}

// ── Serialize back to CSV ─────────────────────────────────

function serializeCSV(rows: string[][]): string {
  return rows.map(row =>
    row.map(cell => {
      const needsQuotes = cell.includes(',') || cell.includes('"') || cell.includes('\n');
      return needsQuotes ? `"${cell.replace(/"/g, '""')}"` : cell;
    }).join(',')
  ).join('\r\n');
}

// ── AI call (via existing /api/claude proxy) ──────────────

const CLAUDE_BASE = '/api/claude';
const BATCH_SIZE  = 50;

async function callClaudeCSV(
  descriptions: string[],
  criteria: string,
  matchMode: MatchMode,
  caseSensitive: boolean,
): Promise<RedactResult[]> {
  const numberedList = descriptions.map((d, i) => `${i}: ${d}`).join('\n');

  const prompt = matchMode === 'exact'
    ? `You are a redaction engine. You will receive a numbered list of record values and a list of EXACT words/phrases to redact.

IMPORTANT RULES:
- ONLY redact words or phrases that EXACTLY match an item from the redaction list (${caseSensitive ? 'case-sensitive — must match exact casing' : 'case-insensitive'}).
- Do NOT redact synonyms, related words, or similar terms.
- Do NOT interpret or expand the list.

WORDS/PHRASES TO REDACT:
${criteria}

RECORD VALUES:
${numberedList}

For each value that contains an exact match, return a JSON object with "index" (row number) and "redact" (array of matched phrases as they appear).
Only include rows that have a match. Return ONLY the JSON array, nothing else. Return [] if nothing matches.`

    : `You are a redaction engine for government public records. You will receive a numbered list of record values and redaction instructions.

Use your judgment to identify words or phrases that match the intent of the instructions, including synonyms, related terms, and anything that fits the spirit of the criteria.

REDACTION INSTRUCTIONS:
${criteria}

RECORD VALUES:
${numberedList}

For each value that contains something matching the criteria, return a JSON object with "index" (row number) and "redact" (array of specific words/phrases to redact — must appear exactly as written in the value).
Only include rows with matches. Return ONLY the JSON array, nothing else. Return [] if nothing matches.`;

  const res = await fetch(`${CLAUDE_BASE}/v1/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-haiku-4-5-20251001',
      max_tokens: 4096,
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`AI request failed: ${res.status} ${err}`);
  }

  const data = await res.json() as { content: { type: string; text: string }[] };
  const text = data.content.find(c => c.type === 'text')?.text?.trim() ?? '';
  const match = text.match(/\[[\s\S]*\]/);
  if (!match) return [];
  try { return JSON.parse(match[0]) as RedactResult[]; }
  catch { return []; }
}

// ── Main component ────────────────────────────────────────

export default function CSVRedactionEditor() {
  const { docId } = useParams<{ docId: string }>();
  const { user }  = useAuthStore();

  // Document + parsed data
  const [doc,          setDoc]          = useState<FoiaDocument | null>(null);
  const [headers,      setHeaders]      = useState<string[]>([]);
  const [parsedRows,   setParsedRows]   = useState<string[][]>([]);
  const [redactedRows, setRedactedRows] = useState<string[][]>([]);
  const [loadError,    setLoadError]    = useState<string | null>(null);
  const [loading,      setLoading]      = useState(true);

  // Redaction controls
  const [selectedCols,  setSelectedCols]  = useState<Set<number>>(new Set());
  const [matchMode,     setMatchMode]     = useState<MatchMode>('ai');
  const [redactScope,   setRedactScope]   = useState<RedactScope>('word');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [criteria,      setCriteria]      = useState('');

  // Run state
  const [running,   setRunning]   = useState(false);
  const [progress,  setProgress]  = useState<{ current: number; total: number } | null>(null);
  const [runError,  setRunError]  = useState<string | null>(null);
  const [log,       setLog]       = useState<LogEntry[]>([]);

  // Output
  const [saving,      setSaving]      = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError,   setSaveError]   = useState<string | null>(null);

  // ── Load document ───────────────────────────────────────

  useEffect(() => {
    if (!docId) return;
    (async () => {
      try {
        const d = await getDocument(Number(docId));
        if (!d) throw new Error('Document not found.');
        setDoc(d);

        const url = await getStaffDownloadUrl(d);
        if (!url) throw new Error('Could not get download URL.');

        const resp = await fetch(`/api/s3download?url=${encodeURIComponent(url)}`);
        if (!resp.ok) throw new Error(`Failed to fetch file: ${resp.status}`);
        const text = await resp.text();

        const allRows = parseCSV(text);
        if (allRows.length < 2) throw new Error('CSV appears to be empty or has only a header row.');

        const hdrs = allRows[0];
        const data = allRows.slice(1);
        setHeaders(hdrs);
        setParsedRows(data);
        setRedactedRows(data.map(r => [...r]));
        setSelectedCols(new Set(hdrs.map((_, i) => i))); // all cols selected by default
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Failed to load CSV.');
      } finally {
        setLoading(false);
      }
    })();
  }, [docId]);

  // ── Run AI redaction ────────────────────────────────────

  async function handleRun() {
    if (!criteria.trim() || selectedCols.size === 0) return;
    setRunning(true);
    setRunError(null);
    setLog([]);
    setSaveSuccess(false);

    // Work on a fresh copy of current redactedRows
    const working = redactedRows.map(r => [...r]);
    const newLog: LogEntry[] = [];
    const cols = Array.from(selectedCols);
    const totalBatches = cols.length * Math.ceil(parsedRows.length / BATCH_SIZE);
    let batchDone = 0;

    try {
      for (const colIdx of cols) {
        const colName = headers[colIdx] ?? `Column ${colIdx + 1}`;

        for (let start = 0; start < parsedRows.length; start += BATCH_SIZE) {
          const batchDescs = parsedRows
            .slice(start, start + BATCH_SIZE)
            .map(r => r[colIdx] ?? '');

          setProgress({ current: ++batchDone, total: totalBatches });

          const results = await callClaudeCSV(batchDescs, criteria, matchMode, caseSensitive);

          for (const item of results) {
            const globalIdx = start + item.index;
            if (globalIdx >= working.length || !Array.isArray(item.redact)) continue;

            const original = working[globalIdx][colIdx] ?? '';
            let result = original;

            if (redactScope === 'cell') {
              result = '***';
            } else {
              for (const phrase of item.redact) {
                const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const regex = new RegExp(escaped, caseSensitive ? 'g' : 'gi');
                result = result.replace(regex, '***');
              }
            }

            if (result !== original) {
              working[globalIdx][colIdx] = result;
              newLog.push({ row: globalIdx + 2, col: colIdx + 1, colName, original, result });
            }
          }
        }
      }

      setRedactedRows(working);
      setLog(newLog);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'AI redaction failed.');
    } finally {
      setRunning(false);
      setProgress(null);
    }
  }

  // ── Reset ───────────────────────────────────────────────

  function handleReset() {
    setRedactedRows(parsedRows.map(r => [...r]));
    setLog([]);
    setSaveSuccess(false);
    setSaveError(null);
  }

  // ── Save redacted copy to GAB ───────────────────────────

  async function handleSave() {
    if (!doc) return;
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const csv  = serializeCSV([headers, ...redactedRows]);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const file = new File([blob], doc.fileName.replace(/\.csv$/i, '_redacted.csv'), { type: 'text/csv' });
      await uploadRedactedVersion(doc.recordId, file, user?.email ?? '');
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  // ── Render helpers ───────────────────────────────────────

  const hasRedactions = log.length > 0;
  const redactedCells = new Set(
    log.map(e => `${e.row - 2}-${e.col - 1}`)
  );

  // ── Loading / error states ───────────────────────────────

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-neutral-50)' }}>
      <IconLoader2 size={28} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-neutral-400)' }} />
    </div>
  );

  if (loadError || !doc) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-neutral-50)' }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <IconAlertCircle size={36} style={{ color: 'var(--cds-red-700)', marginBottom: 16 }} />
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Failed to load document</div>
        <div style={{ fontSize: 14, color: 'var(--color-neutral-500)', marginBottom: 24 }}>{loadError}</div>
        <Link to="/staff/requests" className="btn btn-secondary" style={{ gap: 8 }}>
          <IconArrowLeft size={14} /> Back to Requests
        </Link>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-neutral-50)', display: 'flex', flexDirection: 'column' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'white', borderBottom: '1px solid var(--color-neutral-200)',
        padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16,
        flexShrink: 0,
      }}>
        <Link
          to={doc.isStandalone ? '/staff/redact' : `/staff/requests/${doc.requestId}?tab=documents`}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 14, color: 'var(--color-neutral-500)', textDecoration: 'none' }}
        >
          <IconArrowLeft size={15} /> {doc.isStandalone ? 'Back to Redaction' : 'Back'}
        </Link>
        <div style={{ width: 1, height: 18, background: 'var(--color-neutral-200)' }} />
        <IconTable size={16} style={{ color: 'var(--color-primary-700)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-neutral-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.fileName}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-400)' }}>
            Spreadsheet Redaction · {parsedRows.length.toLocaleString()} rows · {headers.length} columns
          </div>
        </div>
        {hasRedactions && !saveSuccess && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--cds-yellow-700)', background: 'var(--cds-yellow-50)', border: '1px solid #FCD8A3', borderRadius: 4, padding: '4px 10px' }}>
            <IconAlertCircle size={13} />
            {log.length} redaction{log.length !== 1 ? 's' : ''} applied — unsaved
          </div>
        )}
        {saveSuccess && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--cds-green-700)', background: 'var(--cds-green-50)', border: '1px solid var(--cds-green-200)', borderRadius: 4, padding: '4px 10px' }}>
            <IconCheck size={13} /> Redacted copy saved
          </div>
        )}
      </div>

      {/* ── Two-panel body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Left: Spreadsheet table ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 4, overflow: 'auto', maxHeight: 'calc(100vh - 140px)' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%', tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px 10px', background: 'var(--color-neutral-100)', borderBottom: '2px solid var(--color-neutral-200)', fontWeight: 600, fontSize: 12, color: 'var(--color-neutral-500)', textAlign: 'right', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 }}>
                    #
                  </th>
                  {headers.map((h, ci) => (
                    <th
                      key={ci}
                      style={{
                        padding: '8px 10px',
                        background: selectedCols.has(ci) ? '#EFF6FF' : 'var(--color-neutral-100)',
                        borderBottom: '2px solid var(--color-neutral-200)',
                        fontWeight: 600, fontSize: 12,
                        color: selectedCols.has(ci) ? 'var(--color-primary-700)' : 'var(--color-neutral-500)',
                        textAlign: 'left', whiteSpace: 'nowrap',
                        position: 'sticky', top: 0, zIndex: 1,
                      }}
                    >
                      {h || `Col ${ci + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {redactedRows.map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: '1px solid var(--color-neutral-100)' }}>
                    <td style={{ padding: '5px 10px', color: 'var(--color-neutral-300)', fontSize: 12, textAlign: 'right', whiteSpace: 'nowrap', background: 'var(--color-neutral-50)' }}>
                      {ri + 2}
                    </td>
                    {row.map((cell, ci) => {
                      const key = `${ri}-${ci}`;
                      const isRedacted = redactedCells.has(key);
                      const isCellMode = isRedacted && redactScope === 'cell';
                      return (
                        <td
                          key={ci}
                          style={{
                            padding: '5px 10px',
                            maxWidth: 240,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            background: isCellMode ? 'var(--color-neutral-1000)' : isRedacted ? 'var(--cds-orange-50)' : 'transparent',
                            color: isCellMode ? 'var(--color-neutral-1000)' : isRedacted ? '#92400E' : 'var(--color-neutral-700)',
                            fontFamily: isRedacted ? 'monospace' : 'inherit',
                            fontSize: isRedacted ? 11 : 12,
                          }}
                          title={cell}
                        >
                          {isCellMode
                            ? <span style={{ background: 'var(--color-neutral-1000)', color: 'var(--color-neutral-1000)', borderRadius: 2, padding: '1px 4px', fontSize: 12, userSelect: 'none' }}>████████</span>
                            : cell
                          }
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Right: Controls panel ── */}
        <div style={{
          width: 300, flexShrink: 0,
          background: 'white', borderLeft: '1px solid var(--color-neutral-200)',
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto',
        }}>
          <div style={{ padding: '16px 16px 0', flex: 1, overflowY: 'auto' }}>

            {/* Burn button */}
            <div style={{ marginBottom: 16 }}>
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', gap: 8 }}
                onClick={handleSave}
                disabled={saving || !hasRedactions}
                title="Burn redactions into the saved copy"
              >
                {saving
                  ? <><IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Burning…</>
                  : <><IconDeviceFloppy size={14} /> Burn Redactions</>
                }
              </button>
              {saveError && (
                <div style={{ fontSize: 12, color: 'var(--cds-red-700)', display: 'flex', gap: 5, alignItems: 'flex-start', marginTop: 6 }}>
                  <IconAlertCircle size={12} style={{ flexShrink: 0, marginTop: 1 }} /> {saveError}
                </div>
              )}
              {saveSuccess && (
                <div style={{ fontSize: 12, color: 'var(--cds-green-700)', display: 'flex', gap: 5, alignItems: 'center', marginTop: 6 }}>
                  <IconCheck size={12} /> {doc.isStandalone ? 'Redacted copy saved to your library.' : 'Redacted copy saved.'}
                </div>
              )}
            </div>

            <div style={{ height: 1, background: 'var(--color-neutral-100)', marginBottom: 16 }} />

            {/* Column selector */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Target Columns
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {headers.map((h, ci) => (
                  <label key={ci} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', padding: '4px 8px', borderRadius: 5, background: selectedCols.has(ci) ? '#EFF6FF' : 'transparent' }}>
                    <input
                      type="checkbox"
                      checked={selectedCols.has(ci)}
                      onChange={() => setSelectedCols(prev => {
                        const next = new Set(prev);
                        next.has(ci) ? next.delete(ci) : next.add(ci);
                        return next;
                      })}
                      style={{ accentColor: 'var(--color-primary-700)' }}
                    />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selectedCols.has(ci) ? 'var(--color-primary-700)' : 'var(--color-neutral-600)', fontWeight: selectedCols.has(ci) ? 500 : 400 }}>
                      {h || `Column ${ci + 1}`}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--color-neutral-100)', margin: '0 0 16px' }} />

            {/* Match mode */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Match Mode
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['ai', 'exact'] as MatchMode[]).map(m => (
                  <button
                    key={m}
                    onClick={() => setMatchMode(m)}
                    style={{
                      flex: 1, padding: '6px 0', fontSize: 12, fontWeight: 500,
                      borderRadius: 4, cursor: 'pointer', border: '1px solid',
                      borderColor: matchMode === m ? 'var(--color-primary-700)' : 'var(--color-neutral-200)',
                      background: matchMode === m ? 'var(--color-primary-700)' : 'white',
                      color: matchMode === m ? 'white' : 'var(--color-neutral-600)',
                    }}
                  >
                    {m === 'ai' ? 'AI Interpret' : 'Exact Match'}
                  </button>
                ))}
              </div>
              {matchMode === 'exact' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginTop: 8, cursor: 'pointer', color: 'var(--color-neutral-600)' }}>
                  <input
                    type="checkbox"
                    checked={caseSensitive}
                    onChange={e => setCaseSensitive(e.target.checked)}
                    style={{ accentColor: 'var(--color-primary-700)' }}
                  />
                  Case sensitive
                </label>
              )}
            </div>

            {/* Redaction scope */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Redaction Scope
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['word', 'cell'] as RedactScope[]).map(s => (
                  <button
                    key={s}
                    onClick={() => setRedactScope(s)}
                    style={{
                      flex: 1, padding: '6px 0', fontSize: 12, fontWeight: 500,
                      borderRadius: 4, cursor: 'pointer', border: '1px solid',
                      borderColor: redactScope === s ? 'var(--color-neutral-800)' : 'var(--color-neutral-200)',
                      background: redactScope === s ? 'var(--color-neutral-800)' : 'white',
                      color: redactScope === s ? 'white' : 'var(--color-neutral-600)',
                    }}
                  >
                    {s === 'word' ? 'Word / Phrase' : 'Whole Cell'}
                  </button>
                ))}
              </div>
            </div>

            {/* Criteria input */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                {matchMode === 'exact' ? 'Words / Phrases to Redact' : 'Redaction Instructions'}
              </div>
              <textarea
                className="form-input"
                style={{ width: '100%', minHeight: 80, fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }}
                placeholder={matchMode === 'exact'
                  ? 'Enter words or phrases, one per line'
                  : 'e.g. "Redact all personal names and addresses"'
                }
                value={criteria}
                onChange={e => setCriteria(e.target.value)}
              />
            </div>

            {/* Run button */}
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', gap: 8, marginBottom: 8 }}
              onClick={handleRun}
              disabled={running || !criteria.trim() || selectedCols.size === 0}
            >
              {running
                ? <><IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    {progress ? `Batch ${progress.current} of ${progress.total}…` : 'Running…'}</>
                : <><IconPlayerPlay size={14} /> Run Redaction</>
              }
            </button>

            {hasRedactions && (
              <button
                className="btn btn-secondary"
                style={{ width: '100%', justifyContent: 'center', gap: 8, marginBottom: 8, fontSize: 12 }}
                onClick={handleReset}
              >
                <IconX size={13} /> Reset All Redactions
              </button>
            )}

            {runError && (
              <div style={{ fontSize: 12, color: 'var(--cds-red-700)', background: '#FBEFED', border: '1px solid #F7D5D0', borderRadius: 4, padding: '8px 10px', marginBottom: 8, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <IconAlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                {runError}
              </div>
            )}

            {/* Log */}
            {log.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  {log.length} Redaction{log.length !== 1 ? 's' : ''} Applied
                </div>
                <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {log.map((entry, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '5px 8px', background: 'var(--cds-orange-50)', border: '1px solid #FCD8A3', borderRadius: 5 }}>
                      <div style={{ fontWeight: 500, color: '#92400E', marginBottom: 2 }}>
                        Row {entry.row} · {entry.colName}
                      </div>
                      <div style={{ color: 'var(--cds-yellow-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={entry.original}>
                        {entry.original}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}


          </div>
        </div>
      </div>
    </div>
  );
}
